import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ColumnDef } from "@tanstack/react-table";
import { MoreHorizontal, Plus, Trash2, Eye, EyeOff, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

import { api } from "@/lib/api";
import { fetchAll } from "@/lib/fetchAll";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DataTable } from "@/components/data-table/DataTable";
import { StatusBadge } from "@/components/StatusBadge";
import { PageHeader } from "@/components/PageHeader";

export const Route = createFileRoute("/_app/content/pages/")({
  component: PagesIndex,
});

interface PageRow {
  id: number;
  slug: string;
  title: string;
  status: "draft" | "published" | "archived";
  published_at: number | null;
  updated_at: number;
  version: number;
}

function PagesIndex() {
  const { can } = useAuth();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["pages", "list"],
    queryFn: () => fetchAll<PageRow>("/admin/pages"),
  });

  const publish = useMutation({
    mutationFn: (id: number) => api.post(`/admin/pages/${id}/publish`),
    onSuccess: () => { toast.success("Published"); qc.invalidateQueries({ queryKey: ["pages"] }); },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Publish failed"),
  });
  const unpublish = useMutation({
    mutationFn: (id: number) => api.post(`/admin/pages/${id}/unpublish`),
    onSuccess: () => { toast.success("Unpublished"); qc.invalidateQueries({ queryKey: ["pages"] }); },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed"),
  });
  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/admin/pages/${id}`),
    onSuccess: () => { toast.success("Page deleted"); qc.invalidateQueries({ queryKey: ["pages"] }); },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Delete failed"),
  });

  const columns: ColumnDef<PageRow, unknown>[] = [
    {
      accessorKey: "title",
      header: "Title",
      cell: ({ row }) => (
        <Link
          to="/content/pages/$id" as never
          params={{ id: String(row.original.id) }}
          className="font-medium hover:text-primary"
        >
          {row.original.title}
          <span className="ml-2 text-xs text-muted-foreground">/{row.original.slug}</span>
        </Link>
      ),
    },
    { accessorKey: "status", header: "Status", cell: ({ row }) => <StatusBadge status={row.original.status} />, filterFn: (row, _id, value) => row.getValue("status") === value },
    {
      accessorKey: "published_at",
      header: "Published",
      cell: ({ row }) => row.original.published_at ? format(row.original.published_at * 1000, "MMM d, yyyy") : <span className="text-muted-foreground">—</span>,
    },
    {
      accessorKey: "updated_at",
      header: "Updated",
      cell: ({ row }) => format(row.original.updated_at * 1000, "MMM d, yyyy"),
    },
    {
      id: "version",
      header: "Rev",
      cell: ({ row }) => <span className="text-xs text-muted-foreground tabular-nums">v{row.original.version}</span>,
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuItem asChild>
              <Link to="/content/pages/$id" as never params={{ id: String(row.original.id) }}>Edit</Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <a href={`/${row.original.slug}`} target="_blank" rel="noreferrer">
                <ExternalLink className="h-3.5 w-3.5" />
                Preview
              </a>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {row.original.status === "published" ? (
              <DropdownMenuItem disabled={!can("pages", "publish")} onClick={() => unpublish.mutate(row.original.id)}>
                <EyeOff className="h-3.5 w-3.5" />
                Unpublish
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem disabled={!can("pages", "publish")} onClick={() => publish.mutate(row.original.id)}>
                <Eye className="h-3.5 w-3.5" />
                Publish
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              disabled={!can("pages", "delete")}
              onClick={() => {
                if (confirm(`Delete "${row.original.title}"? This cannot be undone.`)) {
                  remove.mutate(row.original.id);
                }
              }}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <div className="space-y-2">
      <PageHeader
        title="Pages"
        description="Long-form content for the public site (about, admissions, departments, etc.)."
        actions={
          can("pages", "create") && (
            <Link to="/content/pages/new" as never>
              <Button>
                <Plus className="h-4 w-4" />
                New page
              </Button>
            </Link>
          )
        }
      />

      <DataTable
        columns={columns}
        data={data?.items ?? []}
        globalSearch
        globalSearchPlaceholder="Search by title or slug…"
        filters={[
          { id: "status", label: "Status", options: [
            { value: "draft", label: "Draft" },
            { value: "published", label: "Published" },
            { value: "archived", label: "Archived" },
          ]},
        ]}
        emptyMessage={isLoading ? "Loading…" : "No pages yet — create your first one."}
      />
    </div>
  );
}
